import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { Section, Heading, Badge, Button } from '../components/UI.jsx';
import { studioData, metadata } from '../data.js';
import { Mail, Phone, MapPin, Instagram, Send, CheckCircle2 } from 'lucide-react';
import { getOptimizedImageUrl } from '../lib/utils.js';

const Contact = () => {
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    const formData = new FormData(e.target);
    const data = Object.fromEntries(formData.entries());

    try {
      const response = await fetch('https://api.dev.auxodev.dk/webform/submit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      });

      if (response.ok) {
        setSubmitted(true);
      }
    } catch (error) {
      console.error('Form submission error:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-[#F1E5DC] selection:bg-[#A8814E] selection:text-white">
      <Helmet>
        <title>Kontakt LK STUDIO - Book EMS &amp; Fysioterapi | LK STUDIO</title>
        <meta name="description" content="Kontakt LK STUDIO på Frederiksberg. Ring, skriv eller book din tid online til personlig EMS træning, fysioterapi eller sportsmassage." />
        <link rel="canonical" href="https://lkstudioems.com/kontakt" />

        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://lkstudioems.com/kontakt" />
        <meta property="og:title" content="Kontakt LK STUDIO - Book EMS &amp; Fysioterapi | LK STUDIO" />
        <meta property="og:description" content="Kontakt LK STUDIO på Frederiksberg. Ring, skriv eller book din tid online til personlig EMS træning, fysioterapi eller sportsmassage." />
        <meta property="og:image" content="https://assets.auxodev.dk/assets/766f1303-2002-4537-8cbb-d8bd7f1c5c0f/2333d257-a4d9-4629-9e4c-4f90d7df46b9.jpg" />
        <meta property="og:site_name" content="LK STUDIO" />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:url" content="https://lkstudioems.com/kontakt" />
        <meta name="twitter:title" content="Kontakt LK STUDIO - Book EMS &amp; Fysioterapi | LK STUDIO" />
        <meta name="twitter:description" content="Kontakt LK STUDIO på Frederiksberg. Ring, skriv eller book din tid online til personlig EMS træning, fysioterapi eller sportsmassage." />
        <meta name="twitter:image" content="https://assets.auxodev.dk/assets/766f1303-2002-4537-8cbb-d8bd7f1c5c0f/2333d257-a4d9-4629-9e4c-4f90d7df46b9.jpg" />
      </Helmet>
      {/* Hero Section */}
      <section className="relative min-h-[85vh] overflow-hidden bg-[#F1E5DC] pt-32 lg:pt-44 pb-16 md:pb-24">
        <div className="container relative z-10 mx-auto px-4 sm:px-6 lg:px-10">
          <div className="max-w-5xl">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
            >
              <Badge className="mb-8">
                Kontakt os
              </Badge>
              <h1 className="font-serif text-5xl font-normal leading-[0.9] tracking-[-0.05em] text-[#1C1B1A] sm:text-7xl lg:text-[7.5rem]">
                Lad os <br />
                <span className="text-[#9A8E81] italic">snakke</span>
              </h1>
              
              <div className="mt-12 grid gap-10 border-t border-[#CEBDAA] pt-12 lg:grid-cols-[1.2fr_0.8fr]">
                <div className="max-w-2xl">
                  <p className="text-xl leading-relaxed text-[#494540] sm:text-2xl">
                    Har du spørgsmål til EMS træning, fysioterapi eller vores forløb? Vi står altid klar til at hjælpe dig.
                  </p>
                  <div className="mt-10 flex flex-wrap gap-4">
                    <Button 
                      variant="primary" 
                      onClick={() => {
                        const el = document.querySelector('form');
                        el?.scrollIntoView({ behavior: 'smooth' });
                      }}
                    >
                      SKRIV TIL OS
                    </Button>
                    <Button 
                      variant="outline"
                      onClick={() => window.location.href = `tel:${studioData.phone}`}
                    >
                      RING NU
                    </Button>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>

        {/* Subtle background element */}
        <div className="absolute right-0 top-0 -z-10 h-full w-1/2 overflow-hidden opacity-30 lg:opacity-80">
          <div className="relative h-full w-full">
            <img 
              src={getOptimizedImageUrl("https://assets.auxodev.dk/assets/766f1303-2002-4537-8cbb-d8bd7f1c5c0f/a7f00779-81c6-4f42-a47b-d67aba2c235a.jpg", { width: 1200, quality: 80 })} 
              className="w-full h-full object-cover grayscale-[0.4]" 
              alt="LK STUDIO Frederiksberg" 
            />
            <div className="absolute inset-0 bg-gradient-to-r from-[#F1E5DC] via-[#F1E5DC]/50 to-transparent" />
          </div>
        </div>
      </section>

      <Section className="pt-0">
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row gap-20">
          {/* Contact Info */}
          <div className="md:w-1/2">
            <div className="space-y-12">
              <div className="flex gap-8 group">
                <div className="w-12 h-12 md:w-16 md:h-16 bg-white rounded-2xl md:rounded-3xl shadow-lg flex items-center justify-center shrink-0 group-hover:bg-[#1C1B1A] group-hover:text-white transition-colors duration-500">
                  <MapPin className="w-5 h-5 md:w-6 md:h-6" />
                </div>
                <div>
                  <h4 className="text-sm font-black uppercase text-[#9A8E81] tracking-widest mb-2">Find os her</h4>
                  <p className="text-lg md:text-2xl font-black text-[#1C1B1A]">{studioData.address}</p>
                  <p className="text-sm md:text-base text-[#494540] font-medium">Tæt på Frederiksberg Allé, metro og busser.</p>
                </div>
              </div>

              <div className="flex gap-8 group">
                <div className="w-12 h-12 md:w-16 md:h-16 bg-white rounded-2xl md:rounded-3xl shadow-lg flex items-center justify-center shrink-0 group-hover:bg-[#1C1B1A] group-hover:text-white transition-colors duration-500">
                  <Mail className="w-5 h-5 md:w-6 md:h-6" />
                </div>
                <div>
                  <h4 className="text-sm font-black uppercase text-[#9A8E81] tracking-widest mb-2">Skriv til os</h4>
                  <a href={`mailto:${studioData.email}`} className="text-base md:text-2xl font-black text-[#1C1B1A] hover:text-[#A8814E] transition-colors underline decoration-[#CEBDAA] underline-offset-4">
                    {studioData.email}
                  </a>
                </div>
              </div>

              <div className="flex gap-8 group">
                <div className="w-12 h-12 md:w-16 md:h-16 bg-white rounded-2xl md:rounded-3xl shadow-lg flex items-center justify-center shrink-0 group-hover:bg-[#1C1B1A] group-hover:text-white transition-colors duration-500">
                  <Instagram className="w-5 h-5 md:w-6 md:h-6" />
                </div>
                <div>
                  <h4 className="text-sm font-black uppercase text-[#9A8E81] tracking-widest mb-2">Følg os</h4>
                  <a 
                    href="https://www.instagram.com/lkstudio_cph/" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-lg md:text-2xl font-black text-[#1C1B1A] hover:text-[#A8814E] transition-colors underline decoration-[#CEBDAA] underline-offset-4"
                  >
                    @lkstudio_cph
                  </a>
                </div>
              </div>
            </div>
          </div>

          {/* Form */}
          <div className="md:w-1/2">
            <div className="bg-white rounded-[4rem] p-8 md:p-16 shadow-2xl border border-[#CEBDAA]/10 relative overflow-hidden">
              {submitted ? (
                <motion.div 
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="text-center py-20"
                >
                  <div className="w-24 h-24 bg-[#1C1B1A] text-white rounded-full flex items-center justify-center mx-auto mb-8">
                    <CheckCircle2 className="w-12 h-12" />
                  </div>
                  <h3 className="text-3xl font-black uppercase mb-4 text-[#1C1B1A]">Tak for din besked!</h3>
                  <p className="text-[#494540] font-medium">Vi vender tilbage til dig hurtigst muligt.</p>
                </motion.div>
              ) : (
                <>
                  <h3 className="text-2xl font-black uppercase mb-8 text-[#1C1B1A]">Send en forespørgsel</h3>
                  <form onSubmit={handleSubmit} className="space-y-6">
                    {/* Antispam Honeypot */}
                    <input type="text" name="website_url" className="sr-only absolute" tabIndex="-1" />
                    
                    <div className="grid grid-cols-2 gap-6">
                      <div className="space-y-2">
                        <input 
                          type="text" 
                          name="first_name" 
                          placeholder="Fornavn"
                          required
                          className="w-full h-14 px-6 rounded-2xl bg-[#F1E5DC]/50 border-none focus:ring-2 focus:ring-[#A8814E] transition-all font-medium placeholder-[#9A8E81]"
                        />
                      </div>
                      <div className="space-y-2">
                        <input 
                          type="text" 
                          name="last_name" 
                          placeholder="Efternavn"
                          required
                          className="w-full h-14 px-6 rounded-2xl bg-[#F1E5DC]/50 border-none focus:ring-2 focus:ring-[#A8814E] transition-all font-medium placeholder-[#9A8E81]"
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <input 
                        type="email" 
                        name="email" 
                        placeholder="Email"
                        required
                        className="w-full h-14 px-6 rounded-2xl bg-[#F1E5DC]/50 border-none focus:ring-2 focus:ring-[#A8814E] transition-all font-medium placeholder-[#9A8E81]"
                      />
                    </div>

                    <div className="space-y-2">
                      <textarea 
                        name="message" 
                        placeholder="Din besked"
                        required
                        rows="6"
                        className="w-full p-6 rounded-3xl bg-[#F1E5DC]/50 border-none focus:ring-2 focus:ring-[#A8814E] transition-all font-medium resize-none placeholder-[#9A8E81]"
                      ></textarea>
                    </div>

                    <Button 
                      type="submit" 
                      variant="primary" 
                      className="w-full h-12 sm:h-16 rounded-2xl text-base sm:text-xl"
                      disabled={loading}
                    >
                      {loading ? 'SENDER...' : 'SEND BESKED'}
                      <Send className="ml-3 w-5 h-5" />
                    </Button>
                  </form>
                </>
              )}
            </div>
            
            <div className="mt-12 p-8 bg-[#CEBDAA]/20 rounded-[3rem] border border-[#CEBDAA]/30">
              <h4 className="text-lg font-black uppercase mb-6 flex items-center gap-3 text-[#1C1B1A]">
                <Phone className="w-6 h-6 text-[#A8814E]" /> Ring til os
              </h4>
              <p className="text-sm md:text-base text-[#494540] font-medium mb-6">Har du brug for hurtigt svar eller vil du booke tid over telefonen?</p>
              <a href={`tel:${studioData.phone}`} className="text-xl md:text-3xl font-black text-[#1C1B1A] hover:text-[#A8814E] transition-colors">
                {studioData.phoneDisplay}
              </a>
            </div>
          </div>
        </div>
      </Section>
    </div>
  );
};

export default Contact;
